import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import { wsRequest, completeProposalStep } from '../../api/ws'
import { NavBar, NavBarButton } from '../../components/NavBar'
import {
  describeItem,
  groupByRisk,
  introductionEdits,
  introductionText,
  isIntroduction,
  blockingCollisions,
  blueprintCredit,
  isWaitingStep,
  isDoneStep,
  stepSetup,
  stepGuidance,
  initialSelection,
  toggle,
  applyItemIds,
  riskMeta,
  type Proposal,
} from './onboardingReview'
import './OnboardingReviewScreen.css'

// The onboarding review surface (docs/onboarding-plan.md P0-4). Shows the whole
// proposed capability bundle at once — seeing the total is the least-authority
// mechanism (onboarding.md §3) — grouped by risk, with sensitive items arriving
// unselected. Apply mints the selected items; Skip declines the proposal. Either
// way we continue to the new agent's detail screen (§5: the proposal surfaces
// inline in the create flow, not as a separate errand).
export function OnboardingReviewScreen({
  proposalID,
  agentInstanceID,
  positionID,
}: {
  proposalID: string
  // When set, finishing lands on the new hire's detail screen; when absent (the
  // resource→jobs sweep), finishing just returns to the previous screen.
  agentInstanceID?: string
  positionID?: string
}) {
  const organizationID = useAppStore((s) => s.organizationID)
  const popNav = useAppStore((s) => s.popNav)
  const pushNav = useAppStore((s) => s.pushNav)

  const [proposal, setProposal] = useState<Proposal | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  // Approver edits to introduction bodies, keyed by item id. Held separately
  // from `proposal` so the drafted original stays visible as the baseline and an
  // untouched notice sends exactly what was proposed.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  // Notes on steps a person is marking done, keyed by item id. Optional — the
  // Facilitator browses to check what actually changed rather than trusting a
  // note — but "I used the token from the old integration" is the kind of thing
  // worth being able to say.
  const [stepNotes, setStepNotes] = useState<Record<string, string>>({})
  const [stepBusy, setStepBusy] = useState<string | null>(null)

  async function markStepDone(itemID: string) {
    if (!organizationID || stepBusy) return
    setStepBusy(itemID)
    setActionError(null)
    try {
      const r = await completeProposalStep(
        organizationID, proposalID, itemID, stepNotes[itemID] ?? '',
      )
      if (r.error) {
        setActionError(r.error)
        return
      }
      // Reflect it locally rather than refetching: the server has already
      // pushed a fresh queue, and re-reading here would race that push.
      setProposal((p) =>
        p
          ? {
              ...p,
              items: p.items.map((i) =>
                i.id === itemID ? { ...i, outcome: 'done' } : i,
              ),
            }
          : p,
      )
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e))
    } finally {
      setStepBusy(null)
    }
  }

  useEffect(() => {
    let cancelled = false
    void wsRequest<{ provisioning_proposal?: Proposal; error?: string }>(
      'provisioning_proposal_get',
      { id: proposalID },
      'provisioning_proposal',
      { kind: 'organization', id: organizationID ?? '', action: 'provisioning_proposal_get' },
    )
      .then((r) => {
        if (cancelled) return
        if (r.error || !r.provisioning_proposal) {
          setLoadError(r.error ?? 'could not load the suggestions')
          return
        }
        setProposal(r.provisioning_proposal)
        setSelected(initialSelection(r.provisioning_proposal.items))
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e))
      })
    return () => {
      cancelled = true
    }
  }, [proposalID, organizationID])

  // Replace this review screen: land on the new hire's detail when we came from
  // agent creation; otherwise (the connection sweep) just return to the previous
  // screen.
  function finish() {
    popNav()
    if (agentInstanceID && positionID) {
      pushNav({ kind: 'agent_detail', agentInstanceID, positionID })
    }
  }

  function apply() {
    setBusy(true)
    setActionError(null)
    void wsRequest<{ provisioning_proposal?: Proposal; error?: string }>(
      'provisioning_proposal_apply',
      {
        id: proposalID,
        item_ids: applyItemIds(selected),
        item_texts: introductionEdits(proposal?.items ?? [], drafts, selected),
      },
      'provisioning_proposal_applied',
      { kind: 'organization', id: organizationID ?? '', action: 'provisioning_proposal_apply' },
    )
      .then((r) => {
        if (r.error) {
          setActionError(r.error)
          setBusy(false)
          return
        }
        // A proposal that is now WAITING ON YOU keeps you here, showing what is
        // left to do (docs/blueprints.md §6). Whisking someone away to an agent
        // detail screen at the exact moment they have been handed a checklist
        // is how a checklist gets forgotten.
        const applied = r.provisioning_proposal
        if (applied?.status === 'waiting_on_you') {
          setProposal(applied)
          setSelected(new Set())
          setBusy(false)
          return
        }
        finish()
      })
      .catch((e) => {
        setActionError(e instanceof Error ? e.message : String(e))
        setBusy(false)
      })
  }

  function skip() {
    setBusy(true)
    // Best-effort decline so the proposal doesn't linger; continue regardless.
    void wsRequest(
      'provisioning_proposal_decline',
      { id: proposalID },
      'provisioning_proposal_declined',
      { kind: 'organization', id: organizationID ?? '', action: 'provisioning_proposal_decline' },
    )
      .catch(() => {})
      .finally(finish)
  }

  const back = <NavBarButton icon="‹" label="Back" onClick={popNav} />

  if (loadError) {
    return (
      <div className="onboarding-review">
        <NavBar title="Suggested setup" leading={back} />
        <div className="onboarding-review__empty">
          Couldn't load suggestions: {loadError}
          <button className="onboarding-review__primary" onClick={finish}>
            Continue
          </button>
        </div>
      </div>
    )
  }

  if (!proposal) {
    return (
      <div className="onboarding-review">
        <NavBar title="Suggested setup" leading={back} />
        <div className="onboarding-review__empty">Looking at the job…</div>
      </div>
    )
  }

  const groups = groupByRisk(proposal.items)
  const nSelected = selected.size
  // Named so the primary action can say what will happen, instead of letting
  // someone press Apply and find out with a half-built hire.
  const blocked = blockingCollisions(proposal.items, selected)

  return (
    <div className="onboarding-review">
      <NavBar title="Suggested setup" leading={back} />
      <div className="onboarding-review__body">
        {blueprintCredit(proposal) && (
          <p className="onboarding-review__credit">
            Based on a shared pattern: <strong>{blueprintCredit(proposal)}</strong>
          </p>
        )}

        {proposal.rationale && (
          <p className="onboarding-review__rationale">{proposal.rationale}</p>
        )}

        {proposal.items.length === 0 ? (
          <div className="onboarding-review__empty">
            No extra setup suggested — this agent starts with the safe defaults.
            <button className="onboarding-review__primary" onClick={finish}>
              Continue
            </button>
          </div>
        ) : (
          <>
            <p className="onboarding-review__hint">
              Pick what this agent should be allowed to do. You can change any of
              this later.
            </p>
            {groups.map((group) => (
              <section key={group.risk} className="onboarding-review__group">
                {group.risk === 'advisory' ? (
                  <>
                    <h3 className="onboarding-review__group-title">For you to do</h3>
                    {group.items.map((it) => {
                      const d = describeItem(it)
                      const setup = stepSetup(it)
                      const guidance = stepGuidance(it)
                      const done = isDoneStep(it)
                      const waiting = isWaitingStep(it)
                      return (
                        <div
                          key={it.id}
                          className={`onboarding-review__advisory${done ? ' onboarding-review__advisory--done' : ''}`}
                        >
                          <div className="onboarding-review__step-title">
                            {done ? '✓ ' : ''}
                            {d.title}
                          </div>
                          {guidance && !done && (
                            <p className="onboarding-review__step-guidance">{guidance}</p>
                          )}
                          {setup && !done && (
                            <pre className="onboarding-review__step-setup">{setup}</pre>
                          )}
                          {waiting && (
                            <div className="onboarding-review__step-actions">
                              <input
                                className="onboarding-review__step-note"
                                placeholder="Anything worth mentioning? (optional)"
                                value={stepNotes[it.id] ?? ''}
                                disabled={stepBusy === it.id}
                                onChange={(e) =>
                                  setStepNotes((n) => ({ ...n, [it.id]: e.target.value }))
                                }
                              />
                              <button
                                className="onboarding-review__step-done"
                                disabled={stepBusy === it.id}
                                onClick={() => void markStepDone(it.id)}
                              >
                                {stepBusy === it.id ? 'Saving…' : "I've done this"}
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </>
                ) : (
                  <>
                    <h3 className="onboarding-review__group-title">
                      {group.risk === 'create' ? (
                        <span className="onboarding-review__tag onboarding-review__tag--safe">
                          New setup
                        </span>
                      ) : group.risk === 'revoke' ? (
                        <span className="onboarding-review__tag onboarding-review__tag--warn">
                          Remove access — no longer needed
                        </span>
                      ) : group.risk === 'introduction' ? (
                        <span className="onboarding-review__tag onboarding-review__tag--safe">
                          What everyone will be told
                        </span>
                      ) : (
                        <span className={`onboarding-review__tag onboarding-review__tag--${riskMeta(group.risk).tone}`}>
                          {riskMeta(group.risk).label}
                        </span>
                      )}
                    </h3>
                    {group.items.map((it) => {
                      const d = describeItem(it)
                      const checked = selected.has(it.id)
                      if (isIntroduction(it)) {
                        const body = drafts[it.id] ?? introductionText(it)
                        return (
                          <div key={it.id} className="onboarding-review__intro">
                            <label className="onboarding-review__item">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={busy}
                                onChange={() => setSelected((s) => toggle(s, it.id, proposal?.items ?? []))}
                              />
                              <span className="onboarding-review__item-text">
                                <span className="onboarding-review__item-title">{d.title}</span>
                                <span className="onboarding-review__item-detail">
                                  Posted to your General chat as you. Edit anything
                                  that doesn't sound right.
                                </span>
                              </span>
                            </label>
                            <textarea
                              className="onboarding-review__intro-body"
                              value={body}
                              disabled={busy || !checked}
                              rows={4}
                              aria-label="What the household will be told"
                              onChange={(e) =>
                                setDrafts((d) => ({ ...d, [it.id]: e.target.value }))
                              }
                            />
                          </div>
                        )
                      }
                      return (
                        <label key={it.id} className="onboarding-review__item">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={busy}
                            onChange={() => setSelected((s) => toggle(s, it.id, proposal?.items ?? []))}
                          />
                          <span className="onboarding-review__item-text">
                            <span className="onboarding-review__item-title">{d.title}</span>
                            {d.detail && (
                              <span className="onboarding-review__item-detail">{d.detail}</span>
                            )}
                            {it.collision && (
                              <span
                                className={`onboarding-review__collision onboarding-review__collision--${
                                  it.collision.severity === 'blocks' ? 'blocks' : 'warns'
                                }`}
                              >
                                {it.collision.message}
                              </span>
                            )}
                          </span>
                        </label>
                      )
                    })}
                  </>
                )}
              </section>
            ))}

            {blocked.length > 0 && (
              <div className="onboarding-review__blocked" role="alert">
                {blocked.length === 1
                  ? "One selected item clashes with something you already have, and will fail. Untick it, or change what you have first."
                  : `${blocked.length} selected items clash with things you already have and will fail. Untick them, or change what you have first.`}
              </div>
            )}

            {actionError && <div className="onboarding-review__error">{actionError}</div>}

            <button
              className="onboarding-review__primary"
              onClick={apply}
              disabled={busy}
            >
              {busy
                ? 'Applying…'
                : nSelected === 0
                  ? 'Apply nothing (skip)'
                  : `Apply ${nSelected} selected`}
            </button>
            <button
              className="onboarding-review__secondary"
              onClick={skip}
              disabled={busy}
            >
              Skip for now
            </button>
          </>
        )}
      </div>
    </div>
  )
}
